import { browser } from "$app/environment";

import ArrowUpRightIcon from "@lucide/svelte/icons/arrow-up-right";
import FileSymlinkIcon from "@lucide/svelte/icons/file-symlink";
import FileTextIcon from "@lucide/svelte/icons/file-text";
import { computeCommandScore } from "bits-ui";

import { PUBLIC_DOCS_SEARCH_URL } from "$env/static/public";
import type {
  CommandGroup,
  CommandItem,
} from "$lib/command-palette/utils/groups";

// The api-gateway serves docs search on its own subdomain per environment,
// so it can't be a relative path; deployments set it per environment in
// .env.development (dev service) and .env.production (prod service).
const DOCS_ORIGIN = "https://modal.com";

/**
 * One hit from the modal-search docs API. Response contract:
 * `search/modal_search/docs/README.md`; `slug` is always a docs-relative
 * path with an optional `#anchor` fragment. `snippet` (HTML with `<mark>`
 * tags) is only populated for passage hits and not used by the palette.
 */
export interface DocsSearchResult {
  slug: string;
  kind: "page" | "section" | "passage";
  title: string;
  /** Root-first display path, e.g. ["Guide", "Volumes", "Mount options"]. */
  breadcrumbs: string[];
}

/** Path of a doc on the docs site itself, for in-site navigation. */
export function docsSitePath(slug: string): string {
  return `/docs/${slug}`;
}

export function docsHref(slug: string): string {
  return `${DOCS_ORIGIN}/docs/${slug}`;
}

export async function searchDocs(
  query: string,
  options: {
    limit: number;
    signal?: AbortSignal;
    /** Restrict hits to one docs path and its descendants, e.g. "guide". */
    prefix?: string;
  },
): Promise<DocsSearchResult[]> {
  const url = new URL(PUBLIC_DOCS_SEARCH_URL);
  url.search = new URLSearchParams({
    q: query,
    limit: String(options.limit),
    ...(options.prefix ? { prefix: options.prefix } : {}),
  }).toString();
  const response = await fetch(url, {
    signal: options.signal,
    // The endpoint is public and cookie-free; keep it cache-friendly.
    credentials: "omit",
  });
  if (!response.ok) {
    throw new Error(`Docs search failed with status ${response.status}`);
  }
  const body = (await response.json()) as { results: DocsSearchResult[] };
  return body.results;
}

/**
 * The docs site palette's scope tabs, mirroring the docs navbar. Each tab
 * searches only its own corpus through the API's `prefix` filter, which
 * applies before ranking and limits, so a tab sees its full result budget
 * rather than whatever survived an unfiltered query. Reference spans the SDK
 * and CLI prefixes (release notes live under `sdk/<lang>/releases`), so it
 * fans out one request per prefix.
 */
export const DOCS_SITE_TABS = [
  { id: "all", label: "All", prefixes: [] },
  { id: "guide", label: "Guide", prefixes: ["guide"] },
  { id: "examples", label: "Examples", prefixes: ["examples"] },
  { id: "reference", label: "Reference", prefixes: ["sdk", "cli"] },
] as const satisfies readonly {
  id: string;
  label: string;
  prefixes: readonly string[];
}[];

export type DocsSiteTab = (typeof DOCS_SITE_TABS)[number]["id"];

function docsTabPrefixes(tab: DocsSiteTab): readonly string[] {
  return DOCS_SITE_TABS.find((t) => t.id === tab)?.prefixes ?? [];
}

/**
 * Whether a slug belongs to a tab's corpus, by the API's own prefix rule: the
 * page itself or a path descendant, never a sibling that merely shares the
 * leading characters (`guidebook` is not under `guide`).
 */
export function isInDocsTab(slug: string, tab: DocsSiteTab): boolean {
  const prefixes = docsTabPrefixes(tab);
  if (prefixes.length === 0) return true;
  return prefixes.some(
    (prefix) =>
      slug === prefix ||
      slug.startsWith(`${prefix}/`) ||
      slug.startsWith(`${prefix}#`),
  );
}

/** Round-robin merge of several rank-ordered lists, best hits first. */
function interleave<T>(lists: readonly (readonly T[])[]): T[] {
  const merged: T[] = [];
  const longest = Math.max(0, ...lists.map((list) => list.length));
  for (let i = 0; i < longest; i++) {
    for (const list of lists) {
      if (i < list.length) merged.push(list[i]);
    }
  }
  return merged;
}

/**
 * `searchDocs` scoped to one of the docs site tabs. A multi-prefix tab runs
 * one request per prefix in parallel and interleaves the ranked lists, since
 * the API returns no scores to merge them by.
 */
export async function searchDocsTab(
  tab: DocsSiteTab,
  query: string,
  options: { limit: number; signal?: AbortSignal },
): Promise<DocsSearchResult[]> {
  const prefixes = docsTabPrefixes(tab);
  if (prefixes.length === 0) return searchDocs(query, options);
  const perPrefix = await Promise.all(
    prefixes.map((prefix) => searchDocs(query, { ...options, prefix })),
  );
  return interleave(perPrefix).slice(0, options.limit);
}

export const OPEN_DOCS_HINT = "Open Docs";

export function docsResultToItem(result: DocsSearchResult): CommandItem {
  return {
    label: result.title,
    // Last breadcrumb is the hit itself; the rest is the location.
    breadcrumb: result.breadcrumbs.slice(0, -1),
    icon: FileSymlinkIcon,
    href: docsHref(result.slug),
    external: true,
    selectHint: OPEN_DOCS_HINT,
    selectIcon: ArrowUpRightIcon,
    keywords: result.breadcrumbs,
  };
}

/**
 * `docsResultToItem` for the docs site's own palette: the row navigates
 * in-site, so it carries no external-link affordance.
 */
export function docsResultToSiteItem(result: DocsSearchResult): CommandItem {
  return {
    label: result.title,
    breadcrumb: result.breadcrumbs.slice(0, -1),
    icon: FileTextIcon,
    href: docsSitePath(result.slug),
    keywords: result.breadcrumbs,
  };
}

/**
 * Fuzzy score of a hit against the query, in the same currency
 * (`computeCommandScore`, 0–1) as the settings search so the two ranked
 * blocks can be ordered against each other. Passage hits where only the
 * body matched score low here — a known bias, since the API ranks by
 * content we don't fetch.
 */
function docsScore(result: DocsSearchResult, query: string): number {
  return Math.max(
    computeCommandScore(result.title, query),
    ...result.breadcrumbs.map((crumb) => computeCommandScore(crumb, query)),
  );
}

/**
 * Groups hits into one `Docs / <section>` group per top-level breadcrumb,
 * preserving the API's ranking order within and across groups. With a query,
 * each group carries the best displayed hit's fuzzy score for ordering
 * against other ranked blocks. The list keys items on `href:label`, so
 * duplicate hits (e.g. the section and its passage) must collapse to one.
 */
export function groupDocsResults(
  results: readonly DocsSearchResult[],
  maxPerGroup: number,
  query?: string,
  /** Prepended to each section heading; "" on the Docs tab, which already says docs. */
  headingPrefix = "Docs / ",
  /** Row for a hit; the docs site's own palette passes `docsResultToSiteItem`. */
  toItem: (result: DocsSearchResult) => CommandItem = docsResultToItem,
): CommandGroup[] {
  const groups = new Map<string, { items: CommandItem[]; score: number }>();
  const seen = new Set<string>();
  for (const result of results) {
    const item = toItem(result);
    // The group heading already names the top-level crumb.
    item.breadcrumb = item.breadcrumb?.slice(1);
    const key = `${item.href}:${item.label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const section = result.breadcrumbs[0] ?? "Results";
    const group = groups.get(section) ?? { items: [], score: 0 };
    if (group.items.length < maxPerGroup) {
      group.items.push(item);
      // Only displayed hits count toward ordering — a hidden hit must not
      // promote the section above a better-matching block.
      if (query) group.score = Math.max(group.score, docsScore(result, query));
      groups.set(section, group);
    }
  }
  return [...groups].map(([section, group]) => ({
    heading: `${headingPrefix}${section}`,
    items: group.items,
    score: group.score,
  }));
}

/**
 * Entry points listed on the Docs tab before a query is typed, below any
 * recents, so the tab is never empty.
 */
export const DOCS_SUGGESTIONS: readonly DocsSearchResult[] = [
  {
    slug: "guide",
    kind: "page",
    title: "Guides",
    breadcrumbs: ["Guides"],
  },
  {
    slug: "examples",
    kind: "page",
    title: "Examples",
    breadcrumbs: ["Examples"],
  },
  {
    // /docs/reference redirects here; the resolved slug also lets the docs
    // site's Reference tab claim this row as one of its own.
    slug: "sdk/py/latest",
    kind: "page",
    title: "API reference",
    breadcrumbs: ["API reference"],
  },
];

const RECENTS_STORAGE_KEY = "cmdk-docs-recents";
const MAX_RECENTS = 5;
export function isDocsSearchResult(value: unknown): value is DocsSearchResult {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as {
    slug?: unknown;
    title?: unknown;
    breadcrumbs?: unknown;
  };
  return (
    typeof entry.slug === "string" &&
    typeof entry.title === "string" &&
    Array.isArray(entry.breadcrumbs) &&
    entry.breadcrumbs.every((crumb: unknown) => typeof crumb === "string")
  );
}

/** Docs opened from the palette, most recent first. */
export function loadDocsRecents(): DocsSearchResult[] {
  if (!browser) return [];
  try {
    const raw = localStorage.getItem(RECENTS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isDocsSearchResult);
  } catch {
    return [];
  }
}

export function rememberDocsRecent(result: DocsSearchResult): void {
  if (!browser) return;
  const recent: DocsSearchResult = {
    slug: result.slug,
    kind: result.kind,
    title: result.title,
    breadcrumbs: result.breadcrumbs,
  };
  const recents = loadDocsRecents().filter((r) => r.slug !== result.slug);
  recents.unshift(recent);
  try {
    localStorage.setItem(
      RECENTS_STORAGE_KEY,
      JSON.stringify(recents.slice(0, MAX_RECENTS)),
    );
  } catch {
    // Storage full or disabled: recents are a nicety, not worth surfacing.
  }
}
